import {
  SessionEvent,
  SessionEventType,
  SessionTimelineEventInput,
} from 'types/session';
import { BreadcrumbPayload } from 'types/models/ErrorTracking';
import { sendSessionStateEvents } from './sessionStateUploader';
import { SESSION_CONFIG } from './config';
import { isSessionStateExpiredError } from './sessionStateErrors';
import { mapTimelineEventsToStateEvents } from './sessionStateEventMapping';
import { resolveSessionIdentity } from './sessionIdentity';
import {
  absorbPendingHistory,
  BufferedEvents,
  buildSessionBreadcrumbs,
  clearStoredSession,
  collectSessionMetadata,
  detachSessionEventListeners,
  enqueueTimelineEvent,
  flushBufferedEvents,
  normalizeSessionPath,
  persistStoredSession,
  readStoredSession,
  restoreBufferedEvents,
  sendLifecycleEventSafely,
  setupSessionEventListeners,
  shouldRotateSessionForUser,
  syncSessionWithServer as syncSessionWithServerRuntime,
} from './sessionManagerRuntimeUtils';

export class SessionManager {
  private sessionId: string;
  private startedAt: string;
  private lastHeartbeat?: string;
  private heartbeatInterval?: number;
  private beforeUnloadHandler?: () => void;
  private visibilityHandler?: () => void;
  private listenersAttached = false;
  private buffer: BufferedEvents = { timeline: [], pageViewDelta: 0 };
  private recentHistory: SessionTimelineEventInput[] = [];
  private currentUserId?: string | null;
  private storedUserId?: string | null;
  private hasStarted = false;
  private assignmentIdOverride?: string;
  private lastSeenAt?: string;

  static pendingHistory: SessionTimelineEventInput[] = [];

  static recordPending(event: SessionTimelineEventInput) {
    SessionManager.pendingHistory.push(event);
    if (SessionManager.pendingHistory.length > 10) {
      SessionManager.pendingHistory.shift();
    }
  }

  constructor() {
    if (typeof window === 'undefined') {
      throw new Error(
        'SessionManager must only be instantiated in the browser'
      );
    }
    this.absorbPending();

    const identity = resolveSessionIdentity(readStoredSession());
    this.sessionId = identity.sessionId;
    this.startedAt = identity.startedAt;
    this.lastSeenAt = identity.lastSeenAt;
    this.storedUserId = identity.storedUserId;
    this.saveSession();
  }

  async start(knownUserId?: string | null): Promise<void> {
    if (this.hasStarted) {
      if (
        typeof knownUserId !== 'undefined' &&
        this.currentUserId !== knownUserId
      ) {
        this.setUserId(knownUserId);
      }
      return;
    }
    if (typeof knownUserId !== 'undefined') {
      this.currentUserId = knownUserId;
    }

    this.absorbPending();

    const rotatedForUser = shouldRotateSessionForUser({
      nextUserId: this.currentUserId,
      storedUserId: this.storedUserId,
    });
    if (rotatedForUser) {
      await this.rotateSession();
    }
    const buffered = this.flushBuffered();
    if (!rotatedForUser) {
      await this.syncSessionWithServer();
    }

    this.setupEventListeners();

    const sent = await this.sendEventSafely({
      type: SessionEventType.START,
      sessionId: this.sessionId,
      timestamp: this.startedAt,
      payload: {
        metadata: collectSessionMetadata(),
        timeline: buffered.timeline,
        pageViewDelta: buffered.pageViewDelta,
      },
    });

    if (!sent) {
      this.restoreBuffered(buffered);
      this.detachEventListeners();
      return;
    }

    this.markSeen(this.startedAt);
    this.hasStarted = true;
    void this.sendStateLogs(buffered.timeline);
    this.startHeartbeat();
  }

  async heartbeat(): Promise<void> {
    if (!this.hasStarted) return;
    this.lastHeartbeat = new Date().toISOString();

    const buffered = this.flushBuffered();
    const sent = await this.sendEventSafely(
      {
        type: SessionEventType.HEARTBEAT,
        sessionId: this.sessionId,
        timestamp: this.lastHeartbeat,
        payload: {
          lastSeenAt: this.lastHeartbeat,
          pageViewDelta: buffered.pageViewDelta,
          timeline: buffered.timeline,
        },
      },
      { preferBeacon: true }
    );

    if (!sent) {
      this.restoreBuffered(buffered);
    } else {
      this.markSeen(this.lastHeartbeat);
      void this.sendStateLogs(buffered.timeline);
    }
  }

  async end(reason: 'explicit' | 'timeout' | 'beforeunload'): Promise<void> {
    const hasWork =
      this.hasStarted ||
      this.buffer.pageViewDelta > 0 ||
      this.buffer.timeline.length > 0;
    if (!hasWork) {
      this.detachEventListeners();
      return;
    }

    const buffered = this.flushBuffered();

    const sent = await this.sendEventSafely({
      type: SessionEventType.END,
      sessionId: this.sessionId,
      timestamp: new Date().toISOString(),
      payload: {
        reason,
        pageViewDelta: buffered.pageViewDelta,
        timeline: buffered.timeline,
      },
    });

    if (!sent) {
      this.restoreBuffered(buffered);
    } else {
      void this.sendStateLogs(buffered.timeline);
    }

    this.stopHeartbeat();
    this.clearSession();
    this.hasStarted = false;
    this.detachEventListeners();
    this.resetSessionState();
  }

  async handleSessionExpired(): Promise<void> {
    const wasActive = this.hasStarted;
    const nextUserId = this.currentUserId ?? undefined;
    await this.rotateSession();
    if (wasActive) {
      await this.start(nextUserId);
    }
  }

  private resetSessionState() {
    this.buffer = { timeline: [], pageViewDelta: 0 };
    this.recentHistory = [];
  }

  setUserId(userId: string | null | undefined) {
    const nextUserId = userId ?? null;
    if (this.currentUserId === nextUserId) {
      return;
    }
    const shouldRotate = shouldRotateSessionForUser({
      nextUserId,
      storedUserId: this.storedUserId,
    });
    this.currentUserId = nextUserId;
    this.saveSession();

    if (shouldRotate) {
      // Events collected for one identity must never be attributed to the next.
      this.resetSessionState();
      const rotation = this.rotateSession();
      const rotatedSessionId = this.sessionId;
      void rotation
        .then(() => {
          if (
            this.sessionId !== rotatedSessionId ||
            this.currentUserId !== nextUserId
          ) {
            return;
          }
          return this.start(nextUserId);
        })
        .catch(() => {
          /* ignore rotation failures */
        });
      return;
    }

    void this.syncSessionWithServer().catch(() => {
      /* ignore sync failures */
    });
  }

  setAssignmentId(assignmentId: string | null | undefined) {
    if (!assignmentId) return;
    this.assignmentIdOverride = assignmentId;
  }

  getSessionId() {
    return this.sessionId;
  }

  getUserId() {
    return this.currentUserId ?? null;
  }

  getSessionHandoff() {
    return {
      sessionId: this.sessionId,
      platform: 'WEB' as const,
    };
  }

  isActive() {
    return this.hasStarted;
  }

  pageView(pathname: string) {
    this.buffer.pageViewDelta += 1;
    this.enqueueTimeline('navigation', {
      pathname: normalizeSessionPath(pathname),
    });
  }

  apiCall(
    endpoint: string,
    method: string,
    statusCode?: number,
    options?: { durationMs?: number; requestId?: string }
  ) {
    this.enqueueTimeline('api_call', {
      endpoint: normalizeSessionPath(endpoint),
      method,
      statusCode,
      durationMs: options?.durationMs,
      requestId: options?.requestId,
    });
  }

  recordConsole(level: 'info' | 'warn' | 'error', message: string) {
    this.enqueueTimeline('console', { level, message });
  }

  recordUserAction(action: string, metadata?: Record<string, unknown>) {
    this.enqueueTimeline('user_action', { action, metadata });
  }

  getBreadcrumbs(
    limit: number = SESSION_CONFIG.TIMELINE_MAX
  ): BreadcrumbPayload[] {
    return buildSessionBreadcrumbs({
      pendingHistory: SessionManager.pendingHistory,
      recentHistory: this.recentHistory,
      bufferedTimeline: this.buffer.timeline,
      limit,
    });
  }

  async flushBufferedEvents(): Promise<void> {
    if (!this.hasStarted) return;
    if (this.buffer.timeline.length === 0 && this.buffer.pageViewDelta === 0) {
      return;
    }

    const snapshot = this.flushBuffered();
    const timestamp = new Date().toISOString();
    const sent = await this.sendEventSafely(
      {
        type: SessionEventType.HEARTBEAT,
        sessionId: this.sessionId,
        timestamp,
        payload: {
          lastSeenAt: timestamp,
          pageViewDelta: snapshot.pageViewDelta,
          timeline: snapshot.timeline,
        },
      },
      { preferBeacon: true }
    );

    if (!sent) {
      this.restoreBuffered(snapshot);
    } else {
      this.markSeen(timestamp);
      void this.sendStateLogs(snapshot.timeline);
    }
  }

  private enqueueTimeline(
    eventType: SessionTimelineEventInput['eventType'],
    data?: Record<string, unknown>,
    occurredAt?: string
  ) {
    enqueueTimelineEvent({
      buffer: this.buffer,
      recentHistory: this.recentHistory,
      eventType,
      data,
      occurredAt,
    });
  }

  private absorbPending() {
    if (SessionManager.pendingHistory.length === 0) return;
    this.recentHistory = absorbPendingHistory({
      recentHistory: this.recentHistory,
      pendingHistory: SessionManager.pendingHistory,
    });
    SessionManager.pendingHistory = [];
  }

  private flushBuffered(): BufferedEvents {
    return flushBufferedEvents(this.buffer);
  }

  private restoreBuffered({ timeline, pageViewDelta }: BufferedEvents) {
    restoreBufferedEvents(this.buffer, { timeline, pageViewDelta });
  }

  private async syncSessionWithServer(): Promise<void> {
    await syncSessionWithServerRuntime({
      sessionId: this.sessionId,
      currentUserId: this.currentUserId,
      onServerSessionIdChanged: (serverSessionId) => {
        this.sessionId = serverSessionId;
        this.startedAt = new Date().toISOString();
      },
      onMarkSeen: (timestamp) => this.markSeen(timestamp),
    });
  }

  private async sendStateLogs(
    events: SessionTimelineEventInput[]
  ): Promise<void> {
    if (!events.length) return;
    const mapped = mapTimelineEventsToStateEvents({
      events,
      sessionId: this.sessionId,
      currentUserId: this.currentUserId,
      assignmentIdOverride: this.assignmentIdOverride,
    });

    try {
      await sendSessionStateEvents(mapped);
    } catch (error) {
      if (isSessionStateExpiredError(error)) {
        this.restoreBuffered({ timeline: events, pageViewDelta: 0 });
        await this.rotateSession();
        void this.start(this.currentUserId ?? undefined);
      } else if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.warn('Failed to send session state logs', error);
      }
    }
  }

  private startHeartbeat(): void {
    if (this.heartbeatInterval) return;
    this.heartbeatInterval = window.setInterval(() => {
      void this.heartbeat().catch(() => {
        /* swallow heartbeat errors */
      });
    }, SESSION_CONFIG.HEARTBEAT_INTERVAL_MS);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatInterval) {
      window.clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = undefined;
    }
  }

  private async sendEventSafely(
    event: SessionEvent,
    options?: { preferBeacon?: boolean }
  ): Promise<boolean> {
    return sendLifecycleEventSafely({
      event,
      currentUserId: this.currentUserId,
      assignmentIdOverride: this.assignmentIdOverride,
      getSessionId: () => this.sessionId,
      onExpired: async () => this.rotateSession(),
      preferBeacon: options?.preferBeacon ?? false,
    });
  }

  private saveSession() {
    const userIdToPersist =
      typeof this.currentUserId === 'undefined'
        ? this.storedUserId
        : this.currentUserId;
    persistStoredSession({
      sessionId: this.sessionId,
      startedAt: this.startedAt,
      lastSeenAt: this.lastSeenAt,
      userId: userIdToPersist,
    });
    this.storedUserId = userIdToPersist;
  }

  private markSeen(timestamp: string) {
    this.lastSeenAt = timestamp;
    this.saveSession();
  }

  private clearSession() {
    clearStoredSession();
    this.storedUserId = undefined;
  }

  private async rotateSession() {
    this.stopHeartbeat();
    this.detachEventListeners();
    const identity = resolveSessionIdentity(null);
    this.sessionId = identity.sessionId;
    this.startedAt = identity.startedAt;
    this.lastSeenAt = identity.lastSeenAt;
    this.hasStarted = false;
    this.saveSession();
    await this.syncSessionWithServer();
  }

  private setupEventListeners() {
    Object.assign(
      this,
      setupSessionEventListeners({
        listenersAttached: this.listenersAttached,
        beforeUnloadHandler: this.beforeUnloadHandler,
        visibilityHandler: this.visibilityHandler,
        onBeforeUnload: () => void this.end('beforeunload'),
        onVisibilityHidden: () => void this.heartbeat(),
      })
    );
  }

  private detachEventListeners() {
    Object.assign(
      this,
      detachSessionEventListeners({
        listenersAttached: this.listenersAttached,
        beforeUnloadHandler: this.beforeUnloadHandler,
        visibilityHandler: this.visibilityHandler,
      })
    );
  }
}

let browserSessionManager: SessionManager | null = null;
export const getSessionManager = () => {
  if (typeof window === 'undefined') return null;
  if (!browserSessionManager) browserSessionManager = new SessionManager();
  return browserSessionManager;
};
